// Trust Centre: policy admin (partners portal, super-partner staff only)
// and the public policy library (trust.coachingculture.com).
//
// Dates are unix seconds, like the rest of the v2 API.

/**
 * public: anyone can read it on the Trust Centre.
 * restricted: listed publicly, full text only after an access request.
 * internal: staff only, never shown on the Trust Centre.
 */
export type TrustPolicyTier = "public" | "restricted" | "internal";

/**
 * "nda" is the mutual NDA accepted on the access-request form: edited and
 * approved like a policy, shown at /nda, never in the policy list. There is
 * at most one, and it is always public.
 */
export type TrustPolicyKind = "policy" | "nda";

/**
 * draft -> in_review -> approved -> superseded
 *            |
 *            +-> draft (returned by the approver with a note)
 * A policy has at most one open version (draft or in_review) at a time.
 */
export type TrustPolicyVersionStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "superseded";

export type TrustPolicyChangeStatus = "pending" | "accepted" | "rejected";

/** A super-partner staff member (partner_user). */
export type TrustPersonDto = {
  id: string;
  name: string;
  email: string;
};

export type TrustPolicyOpenVersionDto = {
  id: string;
  label: string;
  status: "draft" | "in_review";
  pendingChanges: number;
};

export type TrustPolicySummaryDto = {
  id: string;
  slug: string;
  kind: TrustPolicyKind;
  title: string;
  summary: string;
  tier: TrustPolicyTier;
  owner: TrustPersonDto | null;
  approver: TrustPersonDto | null;
  reviewIntervalMonths: number;
  /** The approved version the Trust Centre shows. Null until first approval. */
  currentVersion: { id: string; label: string; approvedAt: number } | null;
  openVersion: TrustPolicyOpenVersionDto | null;
  lastReviewedAt: number | null;
  nextReviewDue: number | null;
  /** No approved version yet, or the review date has passed. */
  isOverdue: boolean;
};

export type TrustPolicyVersionListItemDto = {
  id: string;
  label: string;
  status: TrustPolicyVersionStatus;
  createdAt: number;
  approvedAt: number | null;
};

export type TrustPolicyDetailDto = TrustPolicySummaryDto & {
  versions: TrustPolicyVersionListItemDto[];
};

/**
 * A proposed edit to a draft: replace `findText` (which must occur exactly
 * once in the draft) with `replaceText`. Claude's fact-check proposals and
 * staff suggestions both use this shape.
 */
export type TrustPolicyChangeDto = {
  id: string;
  findText: string;
  replaceText: string;
  reason: string;
  /** Where the fact comes from: a file and line, commit, or document. */
  source: string | null;
  /** "Claude", or the staff member's name. */
  proposedBy: string;
  proposedAt: number;
  status: TrustPolicyChangeStatus;
  decidedBy: TrustPersonDto | null;
  decidedAt: number | null;
  /**
   * Pending, but `findText` no longer occurs exactly once in the draft
   * (someone edited that passage), so it can only be rejected.
   */
  isStale: boolean;
};

export type TrustPolicyVersionDto = {
  id: string;
  policyId: string;
  label: string;
  status: TrustPolicyVersionStatus;
  bodyMarkdown: string;
  /** Increments on every save; send it back to avoid overwriting. */
  revision: number;
  /** What changed and why, written when submitting for approval. */
  changeSummary: string | null;
  /** The approved version this draft started from, if any. */
  basedOn: { id: string; label: string; bodyMarkdown: string } | null;
  createdBy: TrustPersonDto | null;
  createdAt: number;
  submittedBy: TrustPersonDto | null;
  submittedAt: number | null;
  approvedBy: TrustPersonDto | null;
  approvedAt: number | null;
  /** The approver's note when returning a version to draft. */
  returnNote: string | null;
  changes: TrustPolicyChangeDto[];
};

export type TrustAuditEventDto = {
  id: number;
  at: number;
  actor: string;
  action: string;
  policyId: string | null;
  versionId: string | null;
  details: Record<string, unknown>;
};

// Commands

export type CreateTrustPolicyCommand = {
  slug: string;
  /** Defaults to "policy". An "nda" must be public, and only one may exist. */
  kind?: TrustPolicyKind;
  title: string;
  summary: string;
  tier: TrustPolicyTier;
  ownerId: string | null;
  approverId: string | null;
  reviewIntervalMonths?: number;
  /** The first draft. */
  bodyMarkdown: string;
  /** Defaults to "1.0". */
  label?: string;
};

export type UpdateTrustPolicyCommand = {
  title?: string;
  summary?: string;
  tier?: TrustPolicyTier;
  ownerId?: string | null;
  approverId?: string | null;
  reviewIntervalMonths?: number;
};

export type SaveTrustPolicyDraftCommand = {
  bodyMarkdown: string;
  label?: string;
  /** The revision the editor loaded; a mismatch returns 409. */
  revision: number;
};

export type ProposeTrustPolicyChangesCommand = {
  /** Defaults to the caller's name. */
  proposedBy?: string;
  changes: {
    findText: string;
    replaceText: string;
    reason: string;
    source?: string | null;
  }[];
};

export type DecideTrustPolicyChangeCommand = {
  decision: "accept" | "reject";
};

export type SubmitTrustPolicyVersionCommand = {
  changeSummary: string;
};

export type ReturnTrustPolicyVersionCommand = {
  note: string;
};

// Public Trust Centre

export type PublicTrustPolicyDto = {
  slug: string;
  title: string;
  summary: string;
  tier: "public" | "restricted";
  version: string;
  approvedAt: number;
  nextReviewDue: number | null;
  owner: string | null;
  approver: string | null;
  /** Only for public policies. */
  bodyMarkdown?: string;
};

/** The approved mutual NDA, shown at /nda and beside the request form. */
export type PublicTrustNdaDto = {
  title: string;
  /** Short plain-English summary (the policy summary). */
  summary: string;
  version: string;
  approvedAt: number;
  bodyMarkdown: string;
};

// Phase 2: subprocessors and the facts shown on the public overview.

export type TrustSubprocessorDto = {
  id: string;
  name: string;
  purpose: string;
  /** Where the data is stored or processed, e.g. "United Kingdom". */
  dataLocation: string;
  website: string | null;
  /** Lower comes first. */
  position: number;
  updatedAt: number;
};

export type SaveTrustSubprocessorCommand = {
  name: string;
  purpose: string;
  dataLocation: string;
  website?: string | null;
  position?: number;
};

/**
 * Facts staff keep up to date in the portal. Empty means unknown, and the
 * public site hides whatever depends on it rather than showing a guess.
 */
export const TRUST_FACT_KEYS = [
  "securityContactEmail",
  "cyberEssentialsCertificateNumber",
  /** ISO date, e.g. "2027-10-31". */
  "cyberEssentialsValidUntil",
  "cyberEssentialsCertificateUrl",
  "icoRegistrationNumber",
  /** ISO date of the last independent penetration test. */
  "lastPenTestDate",
  /** ISO month or date the next one is booked for, e.g. "2027-01". */
  "nextPenTestDate",
  /** e.g. "Daily, kept for 35 days in a separate AWS account". */
  "backupSummary",
  "dpaUrl",
  "statusPageUrl",
] as const;

export type TrustFactKey = typeof TRUST_FACT_KEYS[number];

export type TrustFactsDto = Record<TrustFactKey, string | null>;

export type UpdateTrustFactsCommand = Partial<Record<TrustFactKey, string | null>>;

export type PublicTrustOverviewDto = {
  facts: TrustFactsDto;
  subprocessors: Omit<TrustSubprocessorDto, "position" | "updatedAt">[];
  /** Most recent approval of any public or restricted policy. */
  policiesLastReviewedAt: number | null;
};

// Phase 3: access to restricted documents.

export type TrustAccessReason =
  | "buying"
  | "annual_review"
  | "partner"
  | "other";

/**
 * How the requester's email matched the platform:
 * customer: active user in an active org (or an email domain listed for it);
 * partner_client: the same, for an org a partner manages;
 * partner: a partner-portal user;
 * trial: a user in a trial org (still needs review);
 * none: no match.
 */
export type TrustAccessMatch =
  | "customer"
  | "partner_client"
  | "partner"
  | "trial"
  | "none";

export type TrustAccessRequestStatus =
  | "pending"
  | "approved"
  | "auto_approved"
  | "declined";

/** Public form. `website` is a honeypot: humans leave it empty. */
export type CreateTrustAccessRequestCommand = {
  name: string;
  email: string;
  jobTitle: string;
  company: string;
  reason: TrustAccessReason;
  ndaAccepted: boolean;
  /**
   * The NDA version the requester was shown (PublicTrustNdaDto.version).
   * Refused if a newer version has been approved since, so the version
   * recorded is always the one they read.
   */
  ndaVersion?: string;
  website?: string;
};

export type TrustAccessRequestDto = {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  company: string;
  reason: TrustAccessReason;
  ndaAcceptedAt: number | null;
  ndaVersion: string | null;
  match: TrustAccessMatch;
  matchedOrg: { id: string; name: string } | null;
  status: TrustAccessRequestStatus;
  decidedBy: TrustPersonDto | null;
  decidedAt: number | null;
  decisionNote: string | null;
  createdAt: number;
  /** The grant this request produced, if approved. */
  grant: { id: string; expiresAt: number; revokedAt: number | null } | null;
};

export type ApproveTrustAccessRequestCommand = {
  /** 30, 90 or 365. */
  durationDays: number;
};

export type DeclineTrustAccessRequestCommand = {
  /** Internal note; not sent to the requester. */
  note?: string | null;
};

export type TrustAccessGrantDto = {
  id: string;
  requestId: string;
  name: string;
  email: string;
  company: string;
  createdAt: number;
  expiresAt: number;
  revokedAt: number | null;
  lastUsedAt: number | null;
  useCount: number;
};

/** Email domains that count as a customer org, e.g. "example.co.uk". */
export type TrustCustomerDomainDto = {
  id: string;
  domain: string;
  org: { id: string; name: string; status: string };
  createdAt: number;
};

export type CreateTrustCustomerDomainCommand = {
  orgId: string;
  domain: string;
};

// Gated access on the public site. The magic link carries a token in the
// URL fragment; the site sends it as the X-Trust-Access header.

export type TrustAccessSessionDto = {
  name: string;
  company: string;
  email: string;
  expiresAt: number;
  documents: {
    slug: string;
    title: string;
    summary: string;
    version: string;
    approvedAt: number;
  }[];
};

export type TrustRestrictedDocumentDto = {
  slug: string;
  title: string;
  version: string;
  approvedAt: number;
  bodyMarkdown: string;
  /** Show on screen, e.g. "Shared with Priya Shah, Northwind Health, 28 Oct 2026". */
  watermark: string;
};

// Phase 4: security FAQ. Pre-written answers to the questions that recur in
// security questionnaires. Edits are made to a working copy; the Trust
// Centre only ever shows the copy someone else approved.

/** public: anyone can read the answer. restricted: the question is listed
 * publicly, the answer only after an access request (NDA). */
export type TrustFaqVisibility = "public" | "restricted";

/**
 * draft: has edits that aren't live (or has never been published);
 * in_review: submitted, waiting for approval;
 * published: live, and the working copy matches it.
 */
export type TrustFaqStatus = "draft" | "in_review" | "published";

export type TrustFaqContent = {
  category: string;
  question: string;
  answerMarkdown: string;
  visibility: TrustFaqVisibility;
  /** Slugs of the policies that back the answer. */
  policySlugs: string[];
};

export type TrustFaqEntryDto = {
  id: string;
  /** Lower comes first. */
  position: number;
  status: TrustFaqStatus;
  /** The working copy. */
  draft: TrustFaqContent;
  /** What the Trust Centre shows. Null until first approval or after unpublishing. */
  published:
    | (TrustFaqContent & { publishedAt: number; approvedBy: TrustPersonDto | null })
    | null;
  /** Staff only: sources, and anything to check. Never published. */
  internalNote: string | null;
  /** Increments on every save; send it back to avoid overwriting. */
  revision: number;
  updatedBy: TrustPersonDto | null;
  updatedAt: number;
  submittedBy: TrustPersonDto | null;
  submittedAt: number | null;
  /** The approver's note when sending an entry back. */
  returnNote: string | null;
};

export type SaveTrustFaqEntryCommand = TrustFaqContent & {
  internalNote?: string | null;
  position?: number;
  /** Required when updating: the revision the editor loaded. */
  revision?: number;
};

export type TrustFaqBatchCommand = { ids: string[] };

export type ReturnTrustFaqEntryCommand = { note: string };

export type TrustFaqBatchResultDto = {
  done: string[];
  /** Entries that couldn't be changed, and why. */
  failed: { id: string; error: string }[];
};

export type PublicTrustFaqEntryDto = {
  id: string;
  category: string;
  question: string;
  visibility: TrustFaqVisibility;
  /** Public entries, or any entry for someone with access. */
  answerMarkdown?: string;
  /** Only policies shown on the Trust Centre. */
  policies: { slug: string; title: string; tier: "public" | "restricted" }[];
  /** When the answer was last approved. */
  reviewedAt: number;
};

// Asset register: the devices and cloud services in scope for Cyber
// Essentials, kept by Coaching Culture staff in the partners portal.

export const TRUST_ASSET_TYPES = [
  "laptop",
  "desktop",
  "phone",
  "tablet",
  "server",
  "network_device",
  "cloud_service",
  "other",
] as const;
export type TrustAssetType = (typeof TRUST_ASSET_TYPES)[number];

/** Company-owned, or a member of staff's own device used for work (BYOD). */
export type TrustAssetOwnership = "company" | "personal";

export type TrustAssetStatus = "active" | "retired" | "disposed";

export type TrustCheckAnswer = "yes" | "no" | "unknown";

/** The Cyber Essentials checks; which ones apply depends on the type. */
export const TRUST_ASSET_CHECKS = [
  /** The OS (or firmware) still gets security updates from its vendor. */
  "osSupported",
  "autoUpdates",
  "firewall",
  /** Anti-malware, or (phones) apps only from the official store. */
  "malwareProtection",
  "diskEncrypted",
  /** Locks automatically and needs a password, PIN or biometric. */
  "screenLock",
  /** Everyday work uses an account without admin rights. */
  "nonAdminDailyUse",
  "defaultPasswordsChanged",
  "unusedSoftwareRemoved",
  /** Cloud services: MFA required for every user. */
  "mfaEnforced",
] as const;
export type TrustAssetCheck = (typeof TRUST_ASSET_CHECKS)[number];

export type TrustAssetChecks = Partial<Record<TrustAssetCheck, TrustCheckAnswer>>;

/** Which checks apply to each type of asset (shared by the API and portal). */
export const TRUST_ASSET_APPLICABLE_CHECKS: Record<TrustAssetType, readonly TrustAssetCheck[]> = {
  laptop: [
    "osSupported",
    "autoUpdates",
    "firewall",
    "malwareProtection",
    "diskEncrypted",
    "screenLock",
    "nonAdminDailyUse",
    "defaultPasswordsChanged",
    "unusedSoftwareRemoved",
  ],
  desktop: [
    "osSupported",
    "autoUpdates",
    "firewall",
    "malwareProtection",
    "diskEncrypted",
    "screenLock",
    "nonAdminDailyUse",
    "defaultPasswordsChanged",
    "unusedSoftwareRemoved",
  ],
  phone: ["osSupported", "autoUpdates", "malwareProtection", "screenLock", "unusedSoftwareRemoved"],
  tablet: ["osSupported", "autoUpdates", "malwareProtection", "screenLock", "unusedSoftwareRemoved"],
  server: [
    "osSupported",
    "autoUpdates",
    "firewall",
    "malwareProtection",
    "nonAdminDailyUse",
    "defaultPasswordsChanged",
    "unusedSoftwareRemoved",
  ],
  network_device: ["osSupported", "autoUpdates", "firewall", "defaultPasswordsChanged"],
  cloud_service: ["mfaEnforced", "nonAdminDailyUse"],
  other: [],
};

export const TRUST_ASSET_CHECK_LABELS: Record<TrustAssetCheck, string> = {
  osSupported: "Still gets security updates",
  autoUpdates: "Automatic updates on",
  firewall: "Firewall on",
  malwareProtection: "Malware protection",
  diskEncrypted: "Disk encrypted",
  screenLock: "Screen lock",
  nonAdminDailyUse: "Everyday account isn't an admin",
  defaultPasswordsChanged: "Default passwords changed",
  unusedSoftwareRemoved: "Unused software removed",
  mfaEnforced: "MFA required for every user",
};

/** Physical and Environmental Security Policy 6.3. */
export const TRUST_ASSET_MAX_LOCK_MINUTES = 5;

/**
 * ok: every check that applies is "yes" and it was confirmed in the last
 * 12 months. attention: something unknown, or not confirmed recently.
 * failing: a check is "no". inactive: retired or disposed.
 */
export type TrustAssetCompliance = "ok" | "attention" | "failing" | "inactive";

export type TrustAssetDto = {
  id: string;
  type: TrustAssetType;
  /** What staff call it, e.g. "Steve's work laptop". */
  name: string;
  ownership: TrustAssetOwnership;
  /** Who uses it (devices) or administers it (cloud services). */
  assignedTo: TrustPersonDto | null;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  /** e.g. "Windows 11 Pro", "macOS", "iOS". */
  osName: string | null;
  /** e.g. "24H2", "15.1". */
  osVersion: string | null;
  /** e.g. "Microsoft Defender". */
  malwareProduct: string | null;
  /** e.g. "Windows Hello PIN", "Touch ID and password". */
  unlockMethod: string | null;
  lockAfterMinutes: number | null;
  checks: TrustAssetChecks;
  notes: string | null;
  status: TrustAssetStatus;
  disposedAt: string | null;
  disposalMethod: string | null;
  /** Wiped or factory reset before it left the company. */
  wiped: boolean | null;
  lastConfirmedAt: number | null;
  lastConfirmedBy: TrustPersonDto | null;
  createdAt: number;
  createdBy: TrustPersonDto | null;
  updatedAt: number;
  compliance: TrustAssetCompliance;
  /** Plain-English reasons for anything other than "ok". */
  complianceReasons: string[];
};

export type TrustAssetFieldChangeDto = {
  field: string;
  from: unknown;
  to: unknown;
};

export type TrustAssetEventDto = {
  id: number;
  at: number;
  actor: string;
  /** created | updated | confirmed | retired | reactivated | disposed */
  action: string;
  changes: TrustAssetFieldChangeDto[];
};

export type TrustAssetDetailDto = {
  asset: TrustAssetDto;
  history: TrustAssetEventDto[];
};

export type SaveTrustAssetCommand = {
  type: TrustAssetType;
  name: string;
  ownership: TrustAssetOwnership;
  assignedToId?: string | null;
  make?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  osName?: string | null;
  osVersion?: string | null;
  malwareProduct?: string | null;
  unlockMethod?: string | null;
  lockAfterMinutes?: number | null;
  checks?: TrustAssetChecks;
  notes?: string | null;
  /** Saving also confirms the details are accurate today. */
  confirm?: boolean;
};

export type SetTrustAssetStatusCommand =
  | { status: "active" | "retired" }
  | {
      status: "disposed";
      /** ISO date, e.g. "2026-11-04". */
      disposedAt: string;
      /** e.g. "Sold", "Recycled via ...", "Returned to supplier". */
      disposalMethod: string;
      wiped: boolean;
    };
